import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProcurementItems from "./Components/ProcurementItems";
import {
  GET_ALL_PROCUREMENT_LIST_REQUEST,
  POST_PROCUREMENT_REQUEST,
  PUT_PROCUREMENT_REQUEST,
} from "../../../../../Utils/Constant";
import ConfirmationATM from "../../../../../Common/ConfirmationATM";
import { hasAddAccess } from "../../../../../Common/CheckAccess";
import { toast } from "react-toastify";

const getInitialState = () => ({
  items: [],
  department_id: "",
  category_id: "",
  priority: "low",
  request_type: "internal"
});

const CreateProcurement = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  // Get procurement data from navigation state
  const procurementData = location.state;

  const [formData, setFormData] = useState(getInitialState());
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [permissions, setPermissions] = useState([]);

  const existingPermissions = localStorage.getItem("checkPermission");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  // Load data from navigation state when editing
  useEffect(() => {
    if (id && procurementData) {
      // Map the procurement data to your form structure
      setFormData({
        items: procurementData.items || [],
        department_id:
          procurementData.department?.id || procurementData.department_id || "",
        category_id:
          procurementData.category?.id || procurementData.category_id || "",
        priority: procurementData.priority || "low",
        purpose: procurementData.purpose || "",
        id: procurementData.id,
        request_type: procurementData.request_type ? "external" : "internal"
        // Add any other fields you need to map
      });
    }
  }, [id, procurementData]);

  const validateFormData = () => {
    
    const errors = {};
    let isValid = true;
    formData.items.forEach((item, index) => {
      const quantity = parseInt(item.quantity || 0, 10);
      const available = parseInt(item.available_quantity || 0, 10);     
        if (quantity > available) {
          errors[`quantity_${index}`] = `Quantity cannot exceed ${available}`;
          isValid = false;
        }
    });
    if (!isValid) {
      toast.error("Please fix quantity errors before submitting.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(formData.request_type==="internal"){
    const isValid = validateFormData();
    if (!isValid) return
    }
    setIsLoading(true);
    try {
      // Prepare the payload in backend-expected format
      const payload = {
        body: { ...formData, priority: "low" },
      };

      if (id) {
        await dispatch({
          type: PUT_PROCUREMENT_REQUEST,
          payload: { requestId: id, ...payload },
        });
      } else {
        await dispatch({
          type: POST_PROCUREMENT_REQUEST,
          payload: payload,
        });
      }
      dispatch({
        type: GET_ALL_PROCUREMENT_LIST_REQUEST,
        payload: { page: 1, limit: 10 },
      });
      setTimeout(() => {
        navigate("/procurements");
      }, 500);
    } catch (error) {
      console.error("Procurement operation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    dispatch({
      type: "CANCELLED_ORDER_REQUEST",
      payload: { id },
    });
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
  };

  return (
    <div className="container md:container-fluid px-4 py-3">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/procurements")}
                title="Back to Procurements"
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <h1 className="h3 mb-0 fw-bold">
                {id
                  ? "Edit Request"
                  : "Create Request"}
              </h1>
              {procurementData && (
                <div className="d-flex gap-2 align-items-center">
                  <span className={`badge ${getStatusBadgeClass(procurementData.status)}`}>
                    {procurementData.status?.charAt(0)?.toUpperCase() +
                      procurementData.status?.slice(1)}
                  </span>
                  <small className="text-muted">
                    {procurementData.department?.name} •{" "}
                    {procurementData.category?.name}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <form>
        {/* Procurement Items */}
        <ProcurementItems
          orderItems={formData}
          setProcurementItems={(items) => setFormData((prev) => items)}
        />

        <div className="d-flex justify-content-end gap-2 mb-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate("/procurements")}
          >
            Cancel
          </button>
          {hasAddAccess(permissions, "inventory") && (<button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading || !formData?.items || formData?.items?.length === 0}
            onClick={handleSubmit}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Processing...
              </>
            ) : id ? (
              "Update Request"
            ) : (
              "Create Request"
            )}
          </button>)}
        </div>
      </form>
      <ConfirmationATM
        show={showCancelModal}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        title="Confirm Request Cancellation"
        message="Are you sure you want to cancel this order?"
      />
    </div>
  );
};

// Helper function for status badge styling
const getStatusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "bg-success"
    case "rejected":
      return "bg-danger"
    case "pending":
      return "bg-warning text-dark"
    default:
      return "bg-secondary"
  }
}

export default CreateProcurement;
